import React, { useState, useEffect, useCallback, useRef } from 'react';
import AIPPT from './AIPPT.jsx';
import { handleAIPPTUpdateCatalogs, handleAIPPTCover } from '@/api/grt/ai';
import { getUserId } from '@/utils/Page';

// 使用示例组件
const AIPPTExample = ({ 
    onGenerate, 
    apiData,
    // 🔥 新增：从父组件传入的参数，用于调用更新接口
    agentId,
    sessionId,
    skillId,
    curSkill,
    conversationId,
    sessionMap,
    curPlugin,
    // 🔥 新增：只读状态
    isReadOnly = false,
    // 🔥 新增：获取聊天数据的方法
    getItem
}) => {
    // 🔥 新增：处理API返回的数据
    const getInitialData = () => {
        if (apiData && apiData.success && apiData.data) {
            return {
                title: apiData.data.title || '未命名PPT',
                catalogs: apiData.data.catalogs || []
            };
        }
        
        // 默认数据
        return {
            title: '网络安全大赛',
            catalogs: [
                {
                    catalog: '网络安全威胁分析',
                    sub_catalog: [
                        '新型网络攻击手段剖析',
                        '网络钓鱼与社交工程防范',
                        '勒索软件与数据保护策略'
                    ]
                },
                {
                    catalog: '安全防护体系建设',
                    sub_catalog: [
                        '防火墙与入侵检测系统部署',
                        '加密技术与数据备份策略',
                        '安全漏洞扫描与应急响应'
                    ]
                }
            ]
        };
    };

    const [pptData, setPptData] = useState(getInitialData());

    // 添加生成状态
    const [isGenerating, setIsGenerating] = useState(false);
    
    // 🔥 新增：防抖定时器引用
    const debounceTimerRef = useRef(null);

    // 🔥 新增：监听apiData变化，自动更新pptData
    useEffect(() => {
        // 🔥 修复：只处理包含catalogs的大纲数据，避免处理封面生成的响应
        if (apiData && apiData.success && apiData.data && apiData.data.catalogs && !isGenerating) {
            setPptData({
                title: apiData.data.title || '未命名PPT',
                catalogs: apiData.data.catalogs || []
            });
            
            // 只有在非生成状态下才重置生成状态（避免在封面生成过程中被重置）
            setIsGenerating(false);
        }
    }, [apiData, isGenerating]);

    // 🔥 修改：实际保存函数（处理会话ID逻辑）
    const actualSaveCatalogsToAPI = useCallback(async (updatedData) => {
        try {
            // 检查是否有API数据和必要的参数
            if (!apiData || !apiData.data || !apiData.data._id) {
                return;
            }

            // 获取用户ID
            const userId = getUserId();
            
            // 🔥 新增：会话ID处理逻辑，优先使用本地存储的会话ID
            const localSessionId = localStorage.getItem('aippt_prev_session_id');
            let finalSessionId = localSessionId || sessionId;
            
            
            // 如果本地和传入的sessionId都无效，才使用apiData中的session_id
            if ((!localSessionId && (!sessionId || sessionId === 0)) && apiData.data.session_id) {
                finalSessionId = apiData.data.session_id;
            }
            
            // 如果仍然没有有效的session_id，使用默认值0
            if (!finalSessionId && finalSessionId !== 0) {
                finalSessionId = 0;
            }
            
            // 构建API请求参数
            const requestData = {
                _id: apiData.data._id, // 使用原始响应的_id
                user_id: userId,
                agent_id: agentId || "1957681408981704706",
                session_id: finalSessionId, // 🔥 使用处理后的会话ID
                skill_id: skillId || "1957685884025380865",
                skill_name: curSkill?.name || "AI PPT",
                skill_describe: curSkill?.description || "",
                apart: curSkill?.apart !== undefined ? curSkill?.apart : true,
                character_setting: "这是一个智能体的角色设定",
                question: {
                    title: updatedData.title,
                    catalogs: updatedData.catalogs
                },
                file_json: [],
                plugin_json: {
                    deep_think: curPlugin?.deep_think || false,
                    web_search: curPlugin?.web_search || false
                },
                // 🔥 修改：session_key的处理逻辑
                session_key: conversationId || sessionMap?.[finalSessionId]?.session_key || ""
            };
            
            
            // 调用更新接口
            const response = await handleAIPPTUpdateCatalogs(requestData);
            
            if (response && response.data) {
                
                // 🔥 新增：如果响应中有新的session_id且与当前不同，可能需要处理
                if (response.data.session_id && response.data.session_id !== finalSessionId) {
                    
                }
            }
            
        } catch (error) {
            console.error('🔥 保存大纲数据时出错:', error);
        }
    }, [apiData, agentId, sessionId, skillId, curSkill, conversationId, sessionMap, curPlugin]);

    // 🔥 新增：防抖版本的保存函数（1秒防抖）
    const saveCatalogsToAPI = useCallback((updatedData) => {
        // 清除之前的定时器
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        
        // 设置新的防抖定时器
        debounceTimerRef.current = setTimeout(() => {
            actualSaveCatalogsToAPI(updatedData);
        }, 1000); // 1秒防抖
    }, [actualSaveCatalogsToAPI]);

    // 🔥 新增：组件卸载时清理定时器
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // 编辑处理函数
    const handleEdit = (type, index, newValue) => {
        // 如果正在生成，不允许编辑
        if (isGenerating) return;
        
        const newData = { ...pptData };
        
        if (type === 'title') {
            newData.title = newValue;
        } else if (type === 'catalog') {
            // 编辑一级大纲
            newData.catalogs[index].catalog = newValue;
        } else if (type === 'sub') {
            // 编辑二级大纲
            const { catalogIndex, subIndex } = index;
            newData.catalogs[catalogIndex].sub_catalog[subIndex] = newValue;
        } else {
            // 兼容旧的调用方式
            const catalogIndex = type;
            const subIndex = index;
            if (catalogIndex === 'title') {
                newData.title = newValue;
            } else if (subIndex === null) {
                newData.catalogs[catalogIndex].catalog = newValue;
            } else {
                newData.catalogs[catalogIndex].sub_catalog[subIndex] = newValue;
            }
        }
        
        setPptData(newData);
        
        // 🔥 新增：失焦时保存数据到API
        saveCatalogsToAPI(newData);
    };

    // 添加章节
    const handleAddSection = (catalogIndex, subIndex = null, type = null) => {
        // 如果正在生成，不允许添加
        if (isGenerating) return;
        
        const newData = { ...pptData };
        
        if (catalogIndex === undefined) {
            // 添加新的一级大纲到末尾
            newData.catalogs.push({
                catalog: '',
                sub_catalog: []
            });
        } else if (type === 'sub') {
            // ⊕按钮：添加子级（二级大纲）
            if (!newData.catalogs[catalogIndex].sub_catalog) {
                newData.catalogs[catalogIndex].sub_catalog = [];
            }
            newData.catalogs[catalogIndex].sub_catalog.push('');
        } else if (type === 'sibling') {
            // ↕按钮：添加同级（一级大纲）到最后面
            newData.catalogs.push({
                catalog: '',
                sub_catalog: []
            });
        } else if (subIndex === null) {
            // 兼容旧的调用方式：添加子级
            if (!newData.catalogs[catalogIndex].sub_catalog) {
                newData.catalogs[catalogIndex].sub_catalog = [];
            }
            newData.catalogs[catalogIndex].sub_catalog.push('');
        } else {
            // 二级大纲中的添加按钮：添加到该一级大纲下所有二级大纲的最后面
            if (!newData.catalogs[catalogIndex].sub_catalog) {
                newData.catalogs[catalogIndex].sub_catalog = [];
            }
            newData.catalogs[catalogIndex].sub_catalog.push('');
        }
        
        setPptData(newData);
        
        // 🔥 新增：添加后保存数据到API
        saveCatalogsToAPI(newData);
    };

    // 删除章节
    const handleDeleteSection = (catalogIndex, subIndex = null) => {
        // 如果正在生成，不允许删除
        if (isGenerating) return;
        
        const newData = { ...pptData };
        
        if (subIndex === null) {
            // 删除一级大纲
            newData.catalogs.splice(catalogIndex, 1);
        } else {
            // 删除二级大纲
            newData.catalogs[catalogIndex].sub_catalog.splice(subIndex, 1);
        }
        
        setPptData(newData);
        
        // 🔥 新增：删除后保存数据到API
        saveCatalogsToAPI(newData);
    };

    // 生成PPT
    const handleGenerate = async (data) => {
        
        // 设置为生成状态，组件变为只读
        setIsGenerating(true);
        
        // 🔥 新增：获取最新的聊天数据
        let chatData = null;
        if (getItem && typeof getItem === 'function') {
            try {
                const chatMessages = getItem();
                
                // 从聊天数据中提取用户的主题信息
                const userMessages = chatMessages.filter(msg => msg.role === 'user');
                if (userMessages.length > 0) {
                    const lastUserMessage = userMessages[userMessages.length - 1];
                    // 提取主题信息
                    if (typeof lastUserMessage.content === 'string') {
                        chatData = {
                            theme: lastUserMessage.content,
                            userInput: lastUserMessage.content
                        };
                    }
                }
            } catch (error) {
                console.error('🔥 获取聊天数据失败:', error);
            }
        }
        
        // 🔥 立即通知父组件添加加载消息到聊天列表
        const theme = chatData?.theme || data.title || "未命名PPT";
        if (onGenerate) {
            const loadingData = {
                type: 'loading', // 标记为加载消息
                theme: theme,
                showLoading: true // 标记需要显示加载状态
            };
            onGenerate(loadingData);
            
            // 🔥 修复：在发送加载消息后，延迟设置生成状态，确保加载消息先显示
            setTimeout(() => {
                setIsGenerating(true);
            }, 100);
        } else {
            setIsGenerating(true); // 如果没有onGenerate回调，直接设置生成状态
        }
        
        try {
            // 获取用户ID
            const userId = getUserId();
            
            // 🔥 新增：会话ID处理逻辑，优先使用本地存储的会话ID
            const localSessionId = localStorage.getItem('aippt_prev_session_id');
            let finalSessionId = localSessionId || sessionId;

            
            // 如果本地和传入的sessionId都无效，才使用apiData中的session_id
            if ((!localSessionId && (!sessionId || sessionId === 0)) && apiData?.data?.session_id) {
                finalSessionId = apiData.data.session_id;
            }
            
            // 如果仍然没有有效的session_id，使用默认值0
            if (!finalSessionId && finalSessionId !== 0) {
                finalSessionId = 0;
            }
            
            // 构建生成PPT封面的请求参数
            const requestData = {
                user_id: userId,
                agent_id: agentId || "1957681408981704706",
                session_id: finalSessionId,
                skill_id: skillId || "1957685884025380865",
                skill_name: curSkill?.name || "通用技能",
                skill_describe: curSkill?.description || "",
                apart: curSkill?.apart !== undefined ? curSkill?.apart : true,
                character_setting: "这是一个智能体的角色设定",
                question: {
                    color: "蓝色",
                    style: "科技风"
                },
                file_json: [],
                plugin_json: {
                    deep_think: curPlugin?.deep_think || false,
                    web_search: curPlugin?.web_search || false
                },
                session_key: conversationId || sessionMap?.[finalSessionId]?.session_key || ""
            };
            
            
            // 调用生成PPT封面接口
            const response = await handleAIPPTCover(requestData);
            
            // 🔥 修改：检查API响应的逻辑，适配实际返回的数据结构
            if (response && response.success && response.data) {
                
                // 检查是否有封面数据
                if (response.data.cover_image_list && response.data.cover_image_list.length > 0) {
                    
                    // 如果传入了onGenerate回调，将响应数据传递给它
                    if (onGenerate) {
                        onGenerate({
                            type: 'template', // 标记为模板选择消息
                            coverResponse: response.data,
                            theme: chatData?.theme || data.title || "未命名PPT",
                            showLoading: false // 标记加载完成
                        });
                    }
                    
                    // 🔥 修复：成功后不要重置生成状态，保持按钮隐藏直到整个流程完成
                    // setIsGenerating(false); // 注释掉，保持生成状态，防止按钮重新出现
                }
            } else {
                
                if (onGenerate) {
                    onGenerate({
                        type: 'error',
                        errorMessage: 'API请求失败，请稍后重试',
                        theme: chatData?.theme || data.title || "未命名PPT"
                    });
                }
            }
            
        } catch (error) {
            console.error('🔥 生成PPT封面时出错:', error);
            // 如果出错，重置生成状态
            setIsGenerating(false);
            
            // 🔥 通知父组件移除加载消息并显示错误
            if (onGenerate) {
                onGenerate({
                    type: 'error', // 标记为错误消息
                    theme: chatData?.theme || data.title || "未命名PPT",
                    showLoading: false,
                    error: true,
                    errorMessage: '生成失败，请重试'
                });
            }
        }
    };

    // 保存大纲
    const handleSave = (data) => {
        alert('大纲已保存！');
    };

    return (
        <div style={{ padding: '12px 8px 12px 8px', background: '#fff', borderRadius: '8px' }}>
        
            <AIPPT
                data={pptData}
                onEdit={handleEdit}
                onAddSection={handleAddSection}
                onDeleteSection={handleDeleteSection}
                onGenerate={handleGenerate}
                onSave={handleSave}
                editable={!isGenerating && !isReadOnly} // 🔥 根据生成状态和只读状态控制是否可编辑
                showGenerateButton={!isGenerating && !isReadOnly} // 🔥 根据生成状态和只读状态控制是否显示生成按钮
                isReadOnly={isReadOnly} // 🔥 传递只读状态到AIPPT组件
            />
        </div>
    );
};

export default AIPPTExample; 